import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { getDestinationByCode } from "shared/data/actions/destinations";
import { AppState } from "shared/data/reducers/index";

type ReturnTypes = {
  isMultiCity: boolean;
  travelFrom: string | null;
  travelTo: string | null;
  departureDate: string;
  returnDate: string;
} | null;

const useSearchInfo = (): ReturnTypes => {
  const router = useRouter();
  const dispatch = useDispatch();

  const [originByCode, setOriginByCode] = useState<string | null>(null);
  const [destinationByCode, setDestinationByCode] = useState<string | null>();

  const {
    defaultAirports,
    airportDestinations,
    countries,
    searchQuery,
  } = useSelector((state: AppState) => ({
    defaultAirports: state.storage.defaultAirportSuggestions,
    airportDestinations: state.storage.airportDestinations,
    searchQuery: state.storage.searchQuery,
    countries: state.storage.countries,
  }));

  const combinedAirportDestinations = [
    ...defaultAirports,
    ...airportDestinations,
  ];
  const isMultiCity =
    Object.keys(searchQuery).includes("flight_1_origin") &&
    Object.keys(searchQuery).includes("flight_1_destination");

  const trafelFromOrigin = isMultiCity
    ? searchQuery.flight_1_origin
    : searchQuery.from;

  const travelToDestination = isMultiCity
    ? searchQuery.flight_1_destination
    : searchQuery.to;

  useEffect(() => {
    if (["/results", "/result"].includes(router.pathname)) {
      (async () => {
        setOriginByCode(
          await getDestinationByCode(
            trafelFromOrigin,
            combinedAirportDestinations
          )(dispatch)?.$t
        );

        setDestinationByCode(
          await getDestinationByCode(
            travelToDestination,
            combinedAirportDestinations
          )(dispatch)?.$t
        );
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!["/results", "/result"].includes(router.pathname)) {
    return null;
  }

  const airportTitle = (airportCode: string) => {
    const defaultAirport = defaultAirports.find(
      (airport) => airport.Code === airportCode
    );

    const defaultAirportLabel = defaultAirport?.label;
    const defaultAirportCountry =
      defaultAirport?.Country && countries[defaultAirport.Country];

    if (defaultAirportLabel && defaultAirportCountry) {
      return {
        airportLabel: defaultAirportLabel,
        airportCountry: defaultAirportCountry,
      };
    }

    const airportDestination = airportDestinations.find(
      (airport) => airport.Code === airportCode
    );

    const airportLabel = airportDestination?.$t;
    const airportCountry =
      airportDestination?.Country && countries[airportDestination.Country];

    return { airportLabel, airportCountry };
  };

  const travelFrom = trafelFromOrigin
    ? airportTitle(
        trafelFromOrigin.includes("+")
          ? trafelFromOrigin.replace("+", "")
          : trafelFromOrigin
      )
    : null;

  const travelTo = travelToDestination
    ? airportTitle(
        travelToDestination.includes("+")
          ? travelToDestination.replace("+", "")
          : travelToDestination
      )
    : null;

  const departureDate = isMultiCity
    ? searchQuery?.flight_1_departureDate
    : searchQuery?.departureDate;

  const returnDate = isMultiCity ? searchQuery?.returnDate : "";

  return {
    isMultiCity,
    travelFrom: travelFrom?.airportLabel ?? originByCode,
    travelTo: travelTo?.airportLabel ?? destinationByCode,
    departureDate,
    returnDate,
  };
};

export default useSearchInfo;
